import { Table } from '@tanstack/react-table';
import { useTranslation } from '@sib/i18n';
import { Button, Badge } from '@sib/ui';
import { useDialog } from '@sib/ui/hooks';
import { X, Settings, Export, DataTableToolbarUpload } from '@sib/ui/icons';
import { DataTableSettingsDialog } from './DataTableSettingsDialog';

interface DataTableSettingsProps<T> {
  table: Table<T>;
  disabledColumns?: Set<string>;
  exportAction?: () => void;
  uploadAction?: () => void;
}

export const DataTableSettings = <T,>({
  table,
  disabledColumns,
  exportAction,
  uploadAction,
}: DataTableSettingsProps<T>) => {
  const { getState, setColumnFilters, setRowSelection } = table;
  const { t } = useTranslation();
  const settingsDialog = useDialog();

  const { rowSelection, columnFilters } = getState();

  const selectedRowsCount = Object.values(rowSelection).filter(Boolean).length;

  const appliedFilters = columnFilters.filter(
    f =>
      f.value !== undefined && f.value !== null && String(f.value).trim() !== ''
  );

  return (
    <>
      <div className="bg-primary sticky top-0 left-0 z-20 flex h-10 w-full items-center gap-2 rounded-tl-sm rounded-tr-sm px-4 pt-2 pb-2 sm:pb-0">
        <Button
          type="button"
          variant="outline"
          className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
          onClick={settingsDialog.handleOpen}
        >
          <Settings className="size-5" />
        </Button>
        {exportAction && (
          <Button
            type="button"
            variant="outline"
            className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
            onClick={exportAction}
            aria-label={t('export')}
          >
            <Export className="pointer-events-none block text-white" />
          </Button>
        )}
        {uploadAction && (
          <Button
            type="button"
            variant="outline"
            className="bg-primary h-7 border-white text-white hover:bg-white/10 hover:text-white"
            onClick={uploadAction}
            aria-label={t('upload')}
          >
            <DataTableToolbarUpload className="pointer-events-none block text-white" />
          </Button>
        )}
        {selectedRowsCount > 0 && (
          <Badge
            variant="secondary"
            className="hover:bg-secondary flex h-7 gap-2 rounded-md text-nowrap"
          >
            {t('selectedRows', {
              count: selectedRowsCount,
            })}
            <Button
              className="flex gap-1 text-xs"
              variant="ghost"
              size="custom"
              onClick={e => {
                e.stopPropagation();
                setRowSelection({});
              }}
            >
              <X className="block h-2 w-2 shrink-0 grow-0" />
              {t('clear')}
            </Button>
          </Badge>
        )}
        {appliedFilters.length > 0 && (
          <div className="flex w-full items-center justify-between">
            <span className="text-sm text-white">
              {t('appliedFilters', {
                count: appliedFilters.length,
              })}
            </span>
            <Button
              className="text-white"
              variant="ghost"
              size="custom"
              onClick={e => {
                e.stopPropagation();
                setColumnFilters([]);
              }}
            >
              <X className="size-3" />
              {t('clearAllFilters')}
            </Button>
          </div>
        )}
      </div>
      <DataTableSettingsDialog
        onClose={settingsDialog.handleClose}
        table={table}
        open={settingsDialog.isOpen}
        disabledColumns={disabledColumns}
      />
    </>
  );
};
